import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { Button, IconName, IconSize, PlainIcon, ProColors } from '../aura';
import { isErrorState, useFormContext } from '../state';
import {
  SegmentEventType,
  useSegmentContext,
} from '../features/segment-context';
import { Body, Title } from './templates';

export type StepErrorTemplate = {
  error?: string;
  onClick: () => void;
};

export function StepErrorTemplate(props: StepErrorTemplate) {
  const { t } = useTranslation('stepError');
  const { error, onClick } = props;

  return (
    <div
      css={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        alignItems: 'center',
        width: '100%',
      })}
    >
      <PlainIcon
        backgroundColor={ProColors.coral[100]}
        size={IconSize.X_LARGE}
        name={IconName.TW_PRO_WARNING_OUTLINE}
        color={ProColors.coral[600]}
        css={css({ padding: 20 })}
      />
      <Title text={t('title')} />
      <Body text={error ? t('bodyWithError', { reason: error }) : t('body')} />
      <Button flavor={'primary'} size={'medium'} onClick={onClick}>
        {t('button')}
      </Button>
    </div>
  );
}

export function StepError() {
  const { state } = useFormContext();
  const { trackEvent } = useSegmentContext();

  const error = useMemo(() => {
    if (isErrorState(state)) {
      if (state.error instanceof Error) {
        return state.error.message;
      } else if (typeof state.error === 'string') {
        return state.error;
      } else {
        return `Unknown error: ${state}`;
      }
    }
  }, [state]);

  useEffect(() => {
    if (isErrorState(state) && error) {
      trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
        step: state.source,
        errors: [error],
      });
    }
  }, [error, state, trackEvent]);

  const onClick = useCallback(() => {
    window.location.reload();
  }, []);

  return <StepErrorTemplate error={error} onClick={onClick} />;
}
